// ---------------------------------------------------------------------------
// locale.store.ts — Zustand store for shared locale state
// ---------------------------------------------------------------------------
// Ensures all components using useTranslation() share the same locale.
// Persists to localStorage under the 'hms-locale' key.
// ---------------------------------------------------------------------------

import { create } from 'zustand';
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  isSupportedLocale,
  type SupportedLocale,
} from './i18n.config';

function getPersistedLocale(): SupportedLocale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  // 1. Check data attribute from SSR (most authoritative)
  try {
    const attr = document.documentElement.getAttribute('data-initial-locale');
    if (attr && isSupportedLocale(attr)) return attr;
  } catch {
    // ignore
  }

  // 2. Check URL path — locale-prefixed routes take priority
  try {
    const firstSegment = window.location.pathname.split('/')[1];
    if (firstSegment && isSupportedLocale(firstSegment)) return firstSegment;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[i18n] Failed to read URL pathname for locale detection:', err);
    }
  }

  // 3. Check localStorage
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isSupportedLocale(stored)) return stored;
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[i18n] Failed to read locale from localStorage:', err);
    }
  }

  // 4. Fallback to cookie (if present)
  try {
    const match = document.cookie.match(new RegExp('(^| )' + LOCALE_STORAGE_KEY + '=([^;]+)'));
    if (match && isSupportedLocale(match[2])) return match[2];
  } catch {
    // ignore
  }

  return DEFAULT_LOCALE;
}

interface LocaleState {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
}

export const useLocaleStore = create<LocaleState>((set) => ({
  locale: getPersistedLocale(),
  setLocale: (locale: SupportedLocale) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, locale);
        // Set cookie for server-side detection (1 year expiry)
        const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `${LOCALE_STORAGE_KEY}=${locale}; expires=${expires}; path=/`;
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[i18n] Failed to persist locale:', err);
        }
      }
    }
    set({ locale });
  },
}));
